import { SetStateAction } from "react";

import * as Constants from "../CommonModels/Constants";
import { CDMRecord } from "../DataOwnerModels/CDMRecord";
import { IDataOwnerRequestDetails } from "../DataOwnerModels/DataOwnerRequestDetails";
import { addPropertyKeyToArray, extractAllPropertyKeysOfContent } from "./extractPropertyKeysOfContent";

export const validateSchema = (
  cdm: CDMRecord,
  cdmIndex: number,
  schemaString: string,
  dataOwnerRequestDetails: IDataOwnerRequestDetails,
  setSchemaError: { (value: SetStateAction<boolean>): void; (arg0: boolean): void; },
  setSchemaErrorText: { (value: SetStateAction<string>): void; (arg0: string): void; },
  setIsJsonArrayType: { (value: SetStateAction<boolean>): void; (arg0: boolean): void; },
) => {
  const cdmWithExistingFlag = cdm as CDMRecord & { isExistingCdm?: boolean | string };

  // Check for empty schema
  if (!schemaString || schemaString?.trim().length <= 0) {
    setSchemaError(true);
    setSchemaErrorText('Please enter Schema in valid JSON format.');
    setIsJsonArrayType(false);
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      schema: '',
      name: '',
      type: Constants.CDM.cdmType1OptionKey,
      expression: null,
      cardinality: 0,
    });
    return;
  }

  // Check for invalid JSON
  let schemaJsonFormat;
  try {
    schemaJsonFormat = JSON.parse(schemaString);
    setSchemaError(false);
    setSchemaErrorText('');
  } catch (e) {
    setSchemaError(true);
    setSchemaErrorText('JSON format is not able to be parsed, please enter a valid JSON format.');
    return;
  }

  // Guard for type json field
  if (!('type' in schemaJsonFormat) || !schemaJsonFormat['type']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid type field in JSON format.');
    return;
  }

  // Guard for unspecified types
  if (schemaJsonFormat['type'] != Constants.CDM.JSchemaObjectType && schemaJsonFormat['type'] != Constants.CDM.JSchemaArrayType) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid type field in JSON format.');
    return;
  }

  // Guard for $id json field in Object types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaObjectType && !schemaJsonFormat['$id']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid $id field in JSON format.');
    return;
  }
  
  // Guard for $items json field in Array types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaArrayType && !schemaJsonFormat['items']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid $items object and $id field in JSON format.');
    return;
  }

  // Guard for $id json field in Array types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaArrayType && !schemaJsonFormat['items']['$id']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid $items object and $id field in JSON format.');
    return;
  }

  // Guard for properties json field in Object types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaObjectType && !schemaJsonFormat['properties']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid properties field in JSON format.');
    return;
  }

  // Guard for properties json field in Array types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaArrayType && !schemaJsonFormat['items']['properties']) {
    setSchemaError(true);
    setSchemaErrorText('Please enter a valid properties field in JSON format.');
    return;
  }

  // Update the CDM
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaArrayType) {
    setIsJsonArrayType(true);
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      name: schemaJsonFormat['items']['$id'] as string,
      schema: schemaString,
    });
  } else if (schemaJsonFormat['type'] == Constants.CDM.JSchemaObjectType) {
    setIsJsonArrayType(false);
    const isExistingCdm =
      cdm.isExistingCdm === true || String(cdm.isExistingCdm || '').trim().toLowerCase() === 'true';
    const hasPersistedCdmType =
      String(cdm.type ?? '') === String(Constants.CDM.cdmType1OptionKey) ||
      String(cdm.type ?? '') === String(Constants.CDM.cdmType2OptionKey);
    const typeResetFields = isExistingCdm || hasPersistedCdmType
      ? {}
      : { type: Constants.CDM.cdmType1OptionKey, expression: '', cardinality: 0 };
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      name: schemaJsonFormat['$id'] as string,
      schema: schemaString,
      ...typeResetFields,
    });
  }

  // Guard for duplicate attribute names in both Object and Array types
  const formattedContent = schemaString?.trim()
  const allPropertyKeys = extractAllPropertyKeysOfContent(formattedContent)
  const aggregatedPropertyKeys: any = []
  allPropertyKeys.forEach((propertyKey: any) => {
    addPropertyKeyToArray(aggregatedPropertyKeys, propertyKey)
  })
  const duplicateKeys = aggregatedPropertyKeys.filter((propertyKey: any) => {
    return propertyKey.occurrence > 1 && !Constants.reservedSchemaKeys.has(propertyKey.key);
  });
  if (duplicateKeys.length > 0) {
    const firstDuplicateAttributeName = duplicateKeys[0].key;
    setSchemaError(true);
    setSchemaErrorText(`The attribute named ${firstDuplicateAttributeName} is not unique, please enter unique attributes.`);
    return;
  }

  // Guard for attribute descriptions in Object types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaObjectType) {
    for (const objectTypeAttributeKey in schemaJsonFormat['properties']) {
      if (!('description' in schemaJsonFormat['properties'][objectTypeAttributeKey]) || 
        !schemaJsonFormat['properties'][objectTypeAttributeKey]['description']) {
        
        setSchemaError(true);
        setSchemaErrorText(`The description for attribute ${objectTypeAttributeKey} is not defined.`);
        return;
      }
    }
  }

  // Guard for attribute descriptions in Array types
  if (schemaJsonFormat['type'] == Constants.CDM.JSchemaArrayType) {
    for (const arrayTypeAttributeKey in schemaJsonFormat['items']['properties']) {
      if (!('description' in schemaJsonFormat['items']['properties'][arrayTypeAttributeKey]) || 
        !schemaJsonFormat['items']['properties'][arrayTypeAttributeKey]['description']) {
        
        setSchemaError(true);
        setSchemaErrorText(`The description for attribute ${arrayTypeAttributeKey} is not defined.`);
        return;
      }
    }
  }
};

export function checkRequiredInProperties(schema: any, path = ''): string[] {
  let errors: string[] = [];

  if (schema && typeof schema === Constants.CDM.JSchemaObjectType) {
    // If this schema has required and properties
    if (Array.isArray(schema.required) && schema.properties) {
      const missing = schema.required.filter(
        (field: string) => !Object.prototype.hasOwnProperty.call(schema.properties, field)
      );
      if (missing.length > 0) {
        errors.push(
          Constants.ERROR_MESSAGES.REQUIRED_MISSING_IN_PROPERTIES(path, missing)
        );
      }
    }

    // Recursively check nested schemas
    if (schema.properties) {
      for (const key in schema.properties) {
        if (Object.prototype.hasOwnProperty.call(schema.properties, key)) {
          errors = errors.concat(checkRequiredInProperties(schema.properties[key], path ? `${path}.${key}` : key));
        }
      }
    }
    if (schema.items) {
      errors = errors.concat(checkRequiredInProperties(schema.items, path ? `${path}.items` : Constants.SCHEMA_KEYS.ITEMS));
    }
    if (Array.isArray(schema.allOf)) {
      schema.allOf.forEach((subSchema: any, idx: number) => {
        errors = errors.concat(checkRequiredInProperties(subSchema, `${path}.allOf[${idx}]`));
      });
    }
    if (Array.isArray(schema.anyOf)) {
      schema.anyOf.forEach((subSchema: any, idx: number) => {
        errors = errors.concat(checkRequiredInProperties(subSchema, `${path}.anyOf[${idx}]`));
      });
    }
    if (Array.isArray(schema.oneOf)) {
      schema.oneOf.forEach((subSchema: any, idx: number) => {
        errors = errors.concat(checkRequiredInProperties(subSchema, `${path}.oneOf[${idx}]`));
      });
    }
    if (schema.not) {
      errors = errors.concat(checkRequiredInProperties(schema.not, `${path}.not`));
    }
  }
  return errors;
}

export function checkMissingType(schema: any, path = ''): string[] {
  let errors: string[] = [];

  const isRootNode = path === '';

  if (schema && typeof schema === Constants.CDM.JSchemaObjectType) {
    const hasType =
      typeof schema.type === Constants.CDM.JSchemaStringType ||
      (Array.isArray(schema.type) && schema.type.every((t: string) => typeof t === Constants.CDM.JSchemaStringType));

    const hasAlternative =
      schema.anyOf || schema.oneOf || schema.allOf || schema.$ref;

    if (!hasType && !hasAlternative) {
      errors.push(Constants.ERROR_MESSAGES.MISSING_TYPE(path));
    }

    // Check for $id only at root level
    if (isRootNode && !schema.$id) {
      errors.push(Constants.ERROR_MESSAGES.MISSING_ID(path));
    }

    if (schema.properties) {
      for (const key in schema.properties) {
        if (Object.prototype.hasOwnProperty.call(schema.properties, key)) {
          errors = errors.concat(checkMissingType(schema.properties[key], `${path}.properties.${key}`));
        }
      }
    }

    if (schema.items) {
      if (Array.isArray(schema.items)) {
        schema.items.forEach((itemSchema: any, idx: number) => {
          errors = errors.concat(checkMissingType(itemSchema, `${path}.items[${idx}]`));
        });
      } else {
        errors = errors.concat(checkMissingType(schema.items, `${path}.items`));
      }
    }

    [Constants.SCHEMA_KEYS.ALLOF, Constants.SCHEMA_KEYS.ANYOF, Constants.SCHEMA_KEYS.ONEOF].forEach((keyword) => {
      if (Array.isArray(schema[keyword])) {
        schema[keyword].forEach((subSchema: any, idx: number) => {
          errors = errors.concat(checkMissingType(subSchema, `${path}.${keyword}[${idx}]`));
        });
      }
    });

    if (schema.not) {
      errors = errors.concat(checkMissingType(schema.not, `${path}.not`));
    }

    if (schema.additionalProperties && typeof schema.additionalProperties === Constants.CDM.JSchemaObjectType) {
      errors = errors.concat(checkMissingType(schema.additionalProperties, `${path}.additionalProperties`));
    }

    if (schema.definitions) {
      for (const key in schema.definitions) {
        if (Object.prototype.hasOwnProperty.call(schema.definitions, key)) {
          errors = errors.concat(checkMissingType(schema.definitions[key], `${path}.definitions.${key}`));
        }
      }
    }
  }

  return errors;
}

export function checkTypeStructure(schema: any, path = ''): string[] {
  let errors: string[] = [];

  if (schema && typeof schema === Constants.CDM.JSchemaObjectType) {
    const type = schema.type;
    const typeList = Array.isArray(type) ? type : typeof type === Constants.CDM.JSchemaStringType ? [type] : [];

    const hasObject = typeList.includes(Constants.CDM.JSchemaObjectType);
    const hasArray = typeList.includes(Constants.CDM.JSchemaArrayType);
    const hasPrimitive = typeList.some(t => [Constants.CDM.JSchemaStringType, Constants.CDM.JSchemaNumberType, Constants.CDM.JSchemaIntegerType, Constants.CDM.JSchemaBooleanType, Constants.CDM.JSchemaNullType].includes(t));

    
    if (hasObject && (!schema.properties || typeof schema.properties !== Constants.CDM.JSchemaObjectType || Array.isArray(schema.properties))){
      errors.push(Constants.ERROR_MESSAGES.MISSING_PROPERTIES_FOR_OBJECT(path));
    }

    if (hasArray && !schema.items) {
      errors.push(Constants.ERROR_MESSAGES.MISSING_ITEMS_FOR_ARRAY(path));
    }

    if (!hasObject && !hasArray && hasPrimitive) {
      if (schema.properties || schema.items) {
        errors.push(Constants.ERROR_MESSAGES.UNEXPECTED_STRUCTURE_FOR_PRIMITIVE(path, typeList));
      }
    }

    // Recurse into nested schemas
    if (schema.properties) {
      for (const key in schema.properties) {
        if (Object.prototype.hasOwnProperty.call(schema.properties, key)) {
          errors = errors.concat(checkTypeStructure(schema.properties[key], `${path}.properties.${key}`));
        }
      }
    }

    if (schema.items) {
      if (Array.isArray(schema.items)) {
        schema.items.forEach((itemSchema: any, idx: number) => {
          errors = errors.concat(checkTypeStructure(itemSchema, `${path}.items[${idx}]`));
        });
      } else {
        errors = errors.concat(checkTypeStructure(schema.items, `${path}.items`));
      }
    }

    [Constants.SCHEMA_KEYS.ALLOF, Constants.SCHEMA_KEYS.ANYOF, Constants.SCHEMA_KEYS.ONEOF].forEach((keyword) => {
      if (Array.isArray(schema[keyword])) {
        schema[keyword].forEach((subSchema: any, idx: number) => {
          errors = errors.concat(checkTypeStructure(subSchema, `${path}.${keyword}[${idx}]`));
        });
      }
    });

    if (schema.not) {
      errors = errors.concat(checkTypeStructure(schema.not, `${path}.not`));
    }

    if (schema.additionalProperties && typeof schema.additionalProperties === Constants.CDM.JSchemaObjectType) {
      errors = errors.concat(checkTypeStructure(schema.additionalProperties, `${path}.additionalProperties`));
    }

    if (schema.definitions) {
      for (const key in schema.definitions) {
        if (Object.prototype.hasOwnProperty.call(schema.definitions, key)) {
          errors = errors.concat(checkTypeStructure(schema.definitions[key], `${path}.definitions.${key}`));
        }
      }
    }
  }
  return errors;
}
